import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    nome_completo: '',
    nome_empresa: '',
    telefone: '',
    cidade_estado: '',
    tipo_servico: '',
    mensagem: '',
    preferencia_contato: 'email' // Novo campo
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Função para aplicar máscara de telefone
  const applyPhoneMask = (value) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Aplica a máscara (31) 97182-3249
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{0,4})(\d{0,4})/, (_, p1, p2, p3) => {
        if (!p2) return `(${p1}`;
        if (!p3) return `(${p1}) ${p2}`;
        return `(${p1}) ${p2}-${p3}`;
      });
    } else {
      return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, `($1) $2-$3`);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Aplicar máscara se for telefone
    if (name === 'telefone') {
      const maskedValue = applyPhoneMask(value);
      setFormData(prev => ({
        ...prev,
        [name]: maskedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Limpar erros ao digitar
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validação básica
    if (!formData.nome_completo || !formData.nome_empresa || !formData.telefone || !formData.cidade_estado || !formData.tipo_servico) {
      setError('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    // Se escolheu WhatsApp, redirecionar
    if (formData.preferencia_contato === 'whatsapp') {
      const mensagem = `
*Nova Solicitação de Orçamento*

👤 *Nome:* ${formData.nome_completo}
🏢 *Empresa:* ${formData.nome_empresa}
📱 *Telefone:* ${formData.telefone}
📍 *Cidade/Estado:* ${formData.cidade_estado}
🎯 *Serviço:* ${formData.tipo_servico}
${formData.mensagem ? `\n💬 *Mensagem:* ${formData.mensagem}` : ''}
      `.trim();
      
      const mensagemEncoded = encodeURIComponent(mensagem);
      window.open(`https://wa.me/5531971823249?text=${mensagemEncoded}`, '_blank');
      
      setSuccess(true);
      setFormData({
        nome_completo: '',
        nome_empresa: '',
        telefone: '',
        cidade_estado: '',
        tipo_servico: '',
        mensagem: '',
        preferencia_contato: 'email'
      });
      
      setTimeout(() => setSuccess(false), 5000);
      return;
    }

    // Se escolheu Email, enviar via API
    setLoading(true);
    setError('');

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Erro ao enviar formulário');
      }

      const data = await response.json();
      
      setSuccess(true);
      // Limpar formulário
      setFormData({
        nome_completo: '',
        nome_empresa: '',
        telefone: '',
        cidade_estado: '',
        tipo_servico: '',
        mensagem: '',
        preferencia_contato: 'email'
      });

      // Ocultar mensagem de sucesso após 5 segundos
      setTimeout(() => setSuccess(false), 5000);

    } catch (err) {
      setError('Erro ao enviar sua solicitação. Por favor, tente novamente ou entre em contato via WhatsApp.');
      console.error('Erro:', err);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section className="py-16 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="container mx-auto px-6">
          <Card className="max-w-2xl mx-auto border-2 border-green-500 shadow-2xl">
            <CardContent className="p-12 text-center">
              <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Obrigado pelo Contato!
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                Recebemos sua mensagem e nossa equipe comercial entrará em contato em breve.
              </p>
              <Button 
                onClick={() => setSuccess(false)}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Enviar Outra Solicitação
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          {/* Título da Seção */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Solicite uma Proposta Comercial
            </h2>
            <p className="text-xl text-gray-600">
              Preencha o formulário abaixo e nossa equipe comercial entrará em contato
            </p>
          </div>

          <Card className="shadow-2xl border-2 border-gray-100">
            <CardContent className="p-8 md:p-12">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Nome Completo */}
                <div>
                  <label htmlFor="nome_completo" className="block text-sm font-bold text-gray-900 mb-2">
                    Nome Completo <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="nome_completo"
                    name="nome_completo"
                    value={formData.nome_completo}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                    placeholder="Digite seu nome completo"
                    required
                  />
                </div>

                {/* Nome da Empresa */}
                <div>
                  <label htmlFor="nome_empresa" className="block text-sm font-bold text-gray-900 mb-2">
                    Nome da Empresa <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="nome_empresa"
                    name="nome_empresa"
                    value={formData.nome_empresa}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                    placeholder="Nome da sua empresa"
                    required
                  />
                </div>

                {/* Grid 2 Colunas */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Telefone */}
                  <div>
                    <label htmlFor="telefone" className="block text-sm font-bold text-gray-900 mb-2">
                      Telefone / WhatsApp <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="telefone"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                      placeholder="(31) 97182-3249"
                      maxLength="15"
                      required
                    />
                  </div>

                  {/* Cidade/Estado */}
                  <div>
                    <label htmlFor="cidade_estado" className="block text-sm font-bold text-gray-900 mb-2">
                      Cidade / Estado <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="cidade_estado"
                      name="cidade_estado"
                      value={formData.cidade_estado}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                      placeholder="Belo Horizonte / MG"
                      required
                    />
                  </div>
                </div>

                {/* Tipo de Serviço */}
                <div>
                  <label htmlFor="tipo_servico" className="block text-sm font-bold text-gray-900 mb-2">
                    Como podemos ajudar? <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="tipo_servico"
                    name="tipo_servico"
                    value={formData.tipo_servico}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white"
                    required
                  >
                    <option value="">Selecione uma opção</option>
                    <option value="Locação de Equipamentos">Locação de Equipamentos</option>
                    <option value="Manutenção/Reforma">Manutenção/Reforma</option>
                    <option value="Compra de Peças">Compra de Peças</option>
                    <option value="Limpeza Pós-Obra">Limpeza Pós-Obra</option>
                    <option value="Outros">Outros</option>
                  </select>
                </div>

                {/* Mensagem Adicional (Opcional) */}
                <div>
                  <label htmlFor="mensagem" className="block text-sm font-bold text-gray-900 mb-2">
                    Mensagem Adicional <span className="text-gray-400 font-normal">(Opcional)</span>
                  </label>
                  <textarea
                    id="mensagem"
                    name="mensagem"
                    value={formData.mensagem}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none"
                    placeholder="Conte-nos mais sobre sua necessidade..."
                  />
                </div>

                {/* Preferência de Contato */}
                <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-xl border-2 border-gray-200">
                  <label className="block text-sm font-bold text-gray-900 mb-4">
                    Como prefere receber nossa resposta?
                  </label>
                  <div className="grid md:grid-cols-2 gap-4">
                    <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.preferencia_contato === 'email' 
                        ? 'border-blue-500 bg-blue-50 shadow-md' 
                        : 'border-gray-300 hover:border-blue-300 bg-white'
                    }`}>
                      <input
                        type="radio"
                        name="preferencia_contato"
                        value="email"
                        checked={formData.preferencia_contato === 'email'}
                        onChange={handleChange}
                        className="w-5 h-5 text-blue-600"
                      />
                      <div>
                        <div className="font-bold text-gray-900">📧 E-mail</div>
                        <div className="text-xs text-gray-600">Receba proposta detalhada por e-mail</div>
                      </div>
                    </label>

                    <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.preferencia_contato === 'whatsapp' 
                        ? 'border-green-500 bg-green-50 shadow-md' 
                        : 'border-gray-300 hover:border-green-300 bg-white'
                    }`}>
                      <input
                        type="radio"
                        name="preferencia_contato"
                        value="whatsapp"
                        checked={formData.preferencia_contato === 'whatsapp'}
                        onChange={handleChange}
                        className="w-5 h-5 text-green-600"
                      />
                      <div>
                        <div className="font-bold text-gray-900">💬 WhatsApp</div>
                        <div className="text-xs text-gray-600">Atendimento rápido via WhatsApp</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Mensagem de Erro */}
                {error && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}

                {/* Botão de Envio */}
                <Button
                  type="submit"
                  disabled={loading}
                  className={`w-full font-bold text-lg py-6 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all ${
                    formData.preferencia_contato === 'whatsapp'
                      ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white'
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white'
                  }`}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      {formData.preferencia_contato === 'whatsapp' ? '💬 Enviar via WhatsApp' : '📧 Receber Proposta por E-mail'}
                    </>
                  )}
                </Button>

                {/* Nota de Privacidade */}
                <p className="text-center text-sm text-gray-500">
                  Seus dados estão seguros e serão usados apenas para entrar em contato com você.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
